import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, normalizeClass as _normalizeClass, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

import { computed, ref, onMounted, nextTick } from "vue";
import { gsap } from "gsap";

import { Doughnut } from "vue-chartjs";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import ListLegend from "~/vue/molecules/lists/ListLegend.vue";

import { getClasses } from "~/js/composables/classes";

// Function to decode HTML entities
interface Dataset {
	label: string;
	values: Array<Number>;
	color: string;
}

interface Props {
	legend: Array<string>;
	dataset: Array<Dataset> | null;
	utilities?: string;
	animate?: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'GraphDoughnut',
  props: {
    legend: {},
    dataset: {},
    utilities: { default: "" },
    animate: { type: Boolean, default: false }
  },
  setup(__props: any) {

// https://vue-chartjs.org/guide/
ChartJS.register(ArcElement, Tooltip, Legend);

const decodeHtmlEntities = (text: string): string => {
	const parser = new DOMParser();
	const decoded = parser.parseFromString(`<!DOCTYPE html><body>${text}`, "text/html").body.textContent;
	return decoded || text;
};

const props = __props;

const classes = ref({
	wrapper: {},
});
const wrapper = ref(null);
const chart = ref(null);

const chartData = computed(() => {
	let sets = props.dataset.map((set, i) => {
		return {
			borderWidth: 0,
			backgroundColor: set.color,
			tooltip: {
				callbacks: {
					label: function (context) {
						return `${context.formattedValue}%`;
					},
				},
			},
			...set,
		};
	});

	return {
		labels: props.legend.map((label) => decodeHtmlEntities(label)),
		datasets: [
			// outer background
			{
				borderWidth: 0,
				backgroundColor: "#F0EFEF", // required for correct legend colors
				data: [1],
				hoverBackgroundColor: "#F0EFEF",
				weight: 0.3,
				pointRadius: 0,
				pointHitRadius: 0,
			},
			// sets
			...sets,
			// inner background
			{
				borderWidth: 0,
				backgroundColor: "#F0EFEF", // required for correct legend colors
				data: [1],
				hoverBackgroundColor: "#F0EFEF",
				weight: 0.3,
				pointRadius: 0,
				pointHitRadius: 0,
			},
		],
	};
});

const chartOptions = computed(() => {
	return {
		cutout: "40%",
		plugins: {
			backgroundBar: false,
			legend: {
				display: false,
			},
		},
		responsive: true,
	};
});

const chartLegend = computed(() => {
	let sets = [];

	props.dataset[0].color.forEach((set, i) => {
		sets.push({
			label: decodeHtmlEntities(props?.legend[i] ?? ""),
			color: set,
		});
	});

	return sets;
});

onMounted(() => {
	if (props.animate) {
		nextTick(() => {
			gsap.from(wrapper.value, {
				scrollTrigger: {
					trigger: wrapper.value,
					start: "-150% top",
					end: "0 top",
				},
				onStart: () => {
					const data = chart.value.chart.data.datasets.map((set) => set.data);
					chart.value.chart.data.datasets.forEach((_, i) => {
						chart.value.chart.data.datasets[i].data = null;
					});
					chart.value.chart.update();
					chart.value.chart.data.datasets.forEach((_, i) => {
						chart.value.chart.data.datasets[i].data = data[i];
					});
					chart.value.chart.update();
				},
				opacity: 0,
			});
		});
	}
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("figure", {
    ref_key: "wrapper",
    ref: wrapper,
    class: _normalizeClass(_unref(getClasses)(classes.value, 'wrapper'))
  }, [
    _createVNode(_unref(Doughnut), {
      ref_key: "chart",
      ref: chart,
      data: chartData.value,
      options: chartOptions.value,
      class: _normalizeClass([props.utilities, '!h-full !w-full'])
    }, null, 8, ["data", "options", "class"]),
    _createVNode(ListLegend, { legend: chartLegend.value }, null, 8, ["legend"])
  ], 2))
}
}

})